// HomeownerWarrantyForm — modal for HO to file a new warranty claim.
//
// Submit flow:
//   1) callWarrantyFn('open_claim', { title, category, description, priority })
//      → returns claim_id
//   2) Upload each photo/file to the homeowner-uploads bucket at
//      {deal_id}/warranty/{claim_id}/{ts}-{filename} via supabaseAnon
//      (lib/warrantyAttachments — HEIC→JPEG, MIME resolution, per-file failures)
//   3) callWarrantyFn('add_photo', { claim_id, photos: [...] }) so the server
//      inserts deal_documents rows + timeline events
//
// Sep 15, 2026: the file input used to carry capture="environment", which on
// iOS/Android jumps straight to the camera and hides the photo library — and
// it only accepted images. Now: Take photo / Photo library / Add a file, with
// PDFs + documents allowed. All notifications fire server-side.

import { useState } from "react";
import { fmtSupabaseError } from "../../lib/fmtError";
import { callWarrantyFn } from "./HomeownerWarrantyCard";
import {
  describeAttachmentCounts,
  describeFailures,
  fmtBytes,
  uploadHomeownerWarrantyAttachments,
} from "../../lib/warrantyAttachments";
import AttachmentPicker from "../shared/AttachmentPicker";

interface Props {
  token: string;
  dealId: string;
  dealName: string | null;
  onClose: () => void;
  onCreated: () => void | Promise<void>;
}

const CATEGORIES = [
  { value: "workmanship", label: "Workmanship (install issue)", helper: "Leaks, missing shingles, flashing problems" },
  { value: "manufacturer", label: "Material defect", helper: "Shingles cracking, color fading, granule loss" },
  { value: "storm_damage", label: "Storm damage", helper: "Wind, hail, fallen tree branches" },
  { value: "other", label: "Other", helper: "Anything else worth a look" },
];

function fileKey(f: File): string {
  return `${f.name}|${f.size}|${f.lastModified}`;
}

export default function HomeownerWarrantyForm({ token, dealId, onClose, onCreated }: Props) {
  const [title, setTitle] = useState("");
  const [category, setCategory] = useState("workmanship");
  const [description, setDescription] = useState("");
  const [files, setFiles] = useState<File[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [stage, setStage] = useState<"form" | "uploading" | "done">("form");
  const [doneNote, setDoneNote] = useState<string | null>(null);

  function addFiles(picked: File[]) {
    setFiles((prev) => {
      const seen = new Set(prev.map(fileKey));
      const next = [...prev];
      for (const f of picked) {
        const k = fileKey(f);
        if (!seen.has(k)) { seen.add(k); next.push(f); }
      }
      return next;
    });
  }

  function removeFile(key: string) {
    setFiles((prev) => prev.filter((f) => fileKey(f) !== key));
  }

  async function handleSubmit() {
    if (!title.trim()) {
      setErr("Please give your issue a short title.");
      return;
    }
    setSubmitting(true);
    setErr(null);
    try {
      // 1) Open the claim
      const open = await callWarrantyFn<{ ok: true; claim_id: string }>(token, "open_claim", {
        title,
        category,
        description,
        priority: "normal",
      });
      const claimId = open.claim_id;

      // 2) Upload attachments (anon-permitted bucket), then 3) register them.
      let note: string | null = null;
      if (files.length > 0) {
        setStage("uploading");
        setProgress({ done: 0, total: files.length });
        const result = await uploadHomeownerWarrantyAttachments({
          dealId,
          claimId,
          files,
          onProgress: (done, total) => setProgress({ done, total }),
        });
        if (result.uploaded.length > 0) {
          await callWarrantyFn(token, "add_photo", { claim_id: claimId, photos: result.uploaded });
        }
        if (result.failures.length > 0) {
          const n = result.failures.length;
          note =
            (result.uploaded.length > 0 ? `We received ${describeAttachmentCounts(result.photoCount, result.documentCount)}. ` : "") +
            `${n === 1 ? "1 file" : `${n} files`} couldn't be attached: ${describeFailures(result.failures)}. ` +
            "You can add more from the Warranty section anytime.";
        }
      }

      setDoneNote(note);
      setStage("done");
      // Brief confirmation pause before close (longer when there's a note to read).
      setTimeout(async () => {
        await onCreated();
      }, note ? 6000 : 1100);
    } catch (e) {
      setErr(fmtSupabaseError(e));
      setStage("form");
    } finally {
      setSubmitting(false);
      setProgress(null);
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 p-0 sm:p-4">
      <div className="bg-white rounded-t-xl sm:rounded-xl shadow-xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="px-4 py-3 border-b border-slate-200 flex items-center justify-between">
          <h2 className="text-base font-semibold text-bardin-ink">Report a warranty issue</h2>
          <button onClick={onClose} disabled={submitting} className="text-slate-400 hover:text-slate-600 disabled:opacity-50">✕</button>
        </div>

        {stage === "done" ? (
          <div className="px-4 py-8 text-center">
            <div className="text-4xl mb-2">✅</div>
            <div className="text-base font-semibold text-bardin-ink mb-1">Claim submitted</div>
            <div className="text-sm text-slate-600">
              Jennifer has been notified. She'll reach out shortly to schedule an inspection.
            </div>
            {doneNote && (
              <div className="mt-3 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded px-2 py-1.5 text-left">
                {doneNote}
              </div>
            )}
          </div>
        ) : (
          <>
            <div className="px-4 py-4 space-y-3">
              <div>
                <label className="text-xs font-medium text-slate-700">What's going on? *</label>
                <input
                  type="text"
                  className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none"
                  placeholder="e.g. Leak in the kitchen ceiling"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  maxLength={120}
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700">Type of issue</label>
                <div className="mt-1 grid grid-cols-1 gap-1.5">
                  {CATEGORIES.map((c) => (
                    <label
                      key={c.value}
                      className={
                        "flex items-start gap-2 px-2.5 py-2 text-sm rounded border cursor-pointer transition " +
                        (category === c.value
                          ? "border-bardin-orange bg-orange-50"
                          : "border-slate-200 hover:bg-slate-50")
                      }
                    >
                      <input
                        type="radio"
                        name="category"
                        value={c.value}
                        checked={category === c.value}
                        onChange={() => setCategory(c.value)}
                        disabled={submitting}
                        className="mt-0.5"
                      />
                      <span className="flex-1 min-w-0">
                        <span className="block font-medium text-bardin-ink">{c.label}</span>
                        <span className="block text-xs text-slate-500">{c.helper}</span>
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700">Tell us more (optional)</label>
                <textarea
                  className="mt-1 w-full px-2.5 py-1.5 text-sm rounded border border-slate-300 focus:border-bardin-teal focus:outline-none min-h-[80px]"
                  placeholder="When did it start? What did you notice?"
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  maxLength={2000}
                  disabled={submitting}
                />
              </div>

              <div>
                <label className="text-xs font-medium text-slate-700">Add photos or files (optional)</label>
                <p className="text-xs text-slate-500 mt-0.5">A few photos of the problem area help us prepare before the visit. PDFs and documents are welcome too.</p>
                <div className="mt-1.5">
                  <AttachmentPicker onFiles={addFiles} disabled={submitting} tone="homeowner" />
                </div>
                {files.length > 0 && (
                  <ul className="mt-2 divide-y divide-slate-100 border border-slate-200 rounded">
                    {files.map((f) => {
                      const k = fileKey(f);
                      return (
                        <li key={k} className="flex items-center gap-2 px-2.5 py-1.5 text-xs">
                          <span className="flex-1 min-w-0 truncate text-slate-700">{f.name}</span>
                          <span className="text-slate-400 flex-shrink-0">{fmtBytes(f.size)}</span>
                          <button
                            type="button"
                            onClick={() => removeFile(k)}
                            disabled={submitting}
                            className="text-slate-400 hover:text-red-600 disabled:opacity-50 px-1"
                            aria-label={`Remove ${f.name}`}
                          >
                            ✕
                          </button>
                        </li>
                      );
                    })}
                  </ul>
                )}
              </div>

              {err && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded px-2 py-1.5">
                  {err}
                </div>
              )}

              {stage === "uploading" && (
                <div className="text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded px-2 py-1.5">
                  Uploading{progress ? ` ${Math.min(progress.done + 1, progress.total)} of ${progress.total}` : ""}…
                </div>
              )}
            </div>

            <div className="px-4 py-3 border-t border-slate-200 flex items-center justify-end gap-2">
              <button onClick={onClose} disabled={submitting} className="px-3 py-1.5 text-xs font-medium rounded bg-slate-200 text-slate-700 hover:bg-slate-300 disabled:opacity-50">
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={submitting || !title.trim()}
                className="px-3 py-1.5 text-xs font-medium rounded bg-bardin-orange text-white hover:bg-bardin-orange/90 disabled:opacity-50"
              >
                {submitting ? "Submitting…" : "Submit claim"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
